import { ApiRequestError, ApiTransportError } from '../api/client';

/**
 * Standard error rendering (ARCHITECTURE_PLAN §10: "Every failed async job and
 * provider error is rendered with its safe message + requestId — no blank
 * failure states"). Accepts any thrown value and renders:
 *   - the safe, actionable message (the server envelope's message for API
 *     errors; a precise local message for transport/contract failures), and
 *   - the request id whenever one exists, so users can quote it to support and
 *     operators can grep Worker logs (§11).
 */

interface DescribedError {
  message: string;
  requestId: string | null;
  /** Extra context shown under the message (e.g. a request that never reached the API). */
  note: string | null;
}

/** Normalize any thrown value into something renderable. Exported for tests. */
export function describeError(error: unknown): DescribedError {
  if (error instanceof ApiRequestError) {
    return { message: error.message, requestId: error.requestId, note: null };
  }
  if (error instanceof ApiTransportError) {
    return {
      message: error.message,
      requestId: error.requestId,
      note: error.requestId === null ? 'The request may not have reached the API.' : null,
    };
  }
  if (error instanceof Error) {
    return { message: error.message, requestId: null, note: null };
  }
  return { message: 'An unexpected error occurred.', requestId: null, note: null };
}

export function ErrorNotice({
  error,
  className = '',
}: {
  error: unknown;
  className?: string;
}): React.JSX.Element {
  const { message, requestId, note } = describeError(error);
  return (
    <div
      role="alert"
      className={`rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 ${className}`}
    >
      <p className="font-medium">{message}</p>
      {requestId !== null && (
        <p className="mt-1">
          Request id: <code className="font-mono text-xs">{requestId}</code>
        </p>
      )}
      {note !== null && <p className="mt-1 text-red-600">{note}</p>}
    </div>
  );
}
